'use client';

import { useMemo, useState } from 'react';
import {
  ProductDetailData,
  ProductReview,
} from '@eliadmin/core/src/api/products/types/product-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProductReviews } from '@eliadmin/core/src/api/products/product-api';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { AddReviewDialog } from '@eliadmin/core/src/components/product/dialog/add-review-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Modal, ModalClose } from '@eliadmin/core/src/components/ui/modal';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { IoIosStar } from 'react-icons/io';
import { LuPlus } from 'react-icons/lu';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import Link from 'next/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

const REVIEWS_PAGE_SIZE = 5;

export function ProductTabsReviews({
  product,
  locale,
}: {
  product: ProductDetailData;
  locale: string;
}) {
  const { t } = useTranslations();

  const me = useCustomerStore(s => s.me);
  const isInitialized = useCustomerStore(s => s.isInitialized);

  const isLogged = !!me && isInitialized;

  const [visibleCount, setVisibleCount] = useState(REVIEWS_PAGE_SIZE);

  const { data: reviewsResponse, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, product.slug],
    queryFn: () => fetchProductReviews({ locale, slug: product.slug }),
  });

  const reviews = useMemo(() => reviewsResponse?.data ?? [], [reviewsResponse]);

  const { averageRating, totalCount, breakdown } = useMemo(() => {
    const totalCount = reviews.length;

    const counts = [0, 0, 0, 0, 0];
    let ratingSum = 0;
    for (const review of reviews) {
      ratingSum += review.rating;
      const bucket = Math.min(5, Math.max(1, Math.round(review.rating)));
      counts[bucket - 1] += 1;
    }

    const breakdown = [5, 4, 3, 2, 1].map(stars => ({
      stars,
      count: counts[stars - 1],
      percentage: totalCount ? (counts[stars - 1] / totalCount) * 100 : 0,
    }));

    const averageRating = totalCount
      ? ratingSum / totalCount
      : typeof product.rating === 'number'
        ? product.rating
        : 0;

    return { averageRating, totalCount, breakdown };
  }, [reviews, product.rating]);

  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;

  return (
    <div className="flex w-full flex-col items-center justify-center gap-6 sm:gap-8 md:gap-12 lg:gap-[50px]">
      <div className="flex w-full items-center justify-between">
        <Heading title={t('text3878')} tag="h3" className={{ container: 'hidden sm:flex' }} />
        <Modal
          trigger={
            <Button className="w-full sm:w-fit" isLoading={!isInitialized}>
              <LuPlus size={20} /> {t('text585')}
            </Button>
          }
        >
          {isLogged ? (
            <AddReviewDialog slug={product.slug} name={product.name} />
          ) : (
            <div className="flex w-full flex-col items-center justify-center gap-6">
              <ButtonIcon
                alt="Wartning"
                url="/images/icons/warning-2.svg"
                className="max-h-12 min-h-12 min-w-12 max-w-12"
              />
              <Heading
                title={t('text3879')}
                tag="h3"
                center
                className={{ container: 'w-full max-w-[600px]', title: 'text-balance' }}
              />
              <ModalClose className="w-full" asChild>
                <Button asChild className="w-full sm:w-fit">
                  <Link href={ROUTES.SIGN_IN()}>{t('text189')}</Link>
                </Button>
              </ModalClose>
            </div>
          )}
        </Modal>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-6 md:flex-row md:justify-between">
        <div className="flex w-full flex-col items-start justify-start gap-6 rounded-[16px] border p-4 sm:p-5 md:max-w-[432px] lg:p-6">
          <div className="flex w-full items-center justify-start gap-6">
            <strong
              className={cn('h1', 'whitespace-nowrap font-semibold tracking-tight text-foreground')}
            >
              {averageRating.toFixed(1)}
            </strong>
            <div className="flex w-full flex-col items-start justify-start gap-1">
              <ProductRating
                rating={averageRating}
                ratingCount={totalCount}
                className={{ starIcon: 'text-[#F5B42F]', starDisabled: 'text-border' }}
              />
              <Paragraph size="base" color="foreground">
                Počet recenzií: {totalCount}
              </Paragraph>
            </div>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-3">
            {breakdown.map(({ stars, count, percentage }) => (
              <div key={stars} className="flex w-full items-center justify-between gap-3">
                <div className="flex items-center justify-start gap-1">
                  <Heading title={stars.toString()} tag="h6" />{' '}
                  <IoIosStar size={20} className="-translate-y-0.5 text-[#F5B42F]" />
                </div>
                <div className="flex h-[4px] w-full flex-1 items-center justify-start overflow-hidden rounded-full bg-border">
                  <div className="h-full bg-primary" style={{ width: `${percentage}%` }} />
                </div>
                <Paragraph className="min-w-6 max-w-6" color="foreground" size="base">
                  {count}
                </Paragraph>
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-3 md:max-w-[744px]">
          {isLoading && <Spinner />}
          {!isLoading && visibleReviews.length === 0 && (
            <Paragraph size="base" color="foreground">
              {t('text608')}
            </Paragraph>
          )}
          {visibleReviews.map(review => (
            <ProductReviewCard
              key={`${review.email}-${review.created_at}`}
              review={review}
              locale={locale}
            />
          ))}
          {hasMore && (
            <Button
              variant={'primary-foreground'}
              className="w-full sm:w-fit"
              onClick={() => setVisibleCount(count => count + REVIEWS_PAGE_SIZE)}
            >
              {t('text490')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductReviewCard({ review, locale }: { review: ProductReview; locale: string }) {
  return (
    <div className="flex w-full flex-col items-start justify-start gap-3 rounded-[16px] border p-4 sm:p-5 lg:p-6">
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start justify-start gap-1">
          <Heading tag="h5" title={`${review.name} ${review.surname}`.trim()} />
          <ProductRating rating={review.rating} hideTextRating />
        </div>
        <Paragraph size="sm" color="foreground" className="font-semibold">
          {new Date(review.created_at).toLocaleDateString(locale)}
        </Paragraph>
      </div>
      <Paragraph size="base" color="foreground" innerHtml={review.review} />
    </div>
  );
}
